import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { getFoodConfigVersions } from 'features/food/foodSlice'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { BodyText, OtherText } from 'styling/Typography'
import { IFoodConfigVersion } from 'types/api/foodApi.interface'
import { foodProvisioningModeLabel, FOOD_UNKNOWN_PRESENCE_OPTIONS } from '../foodPlanningData'

const unknownPresenceLabel = (value: number) =>
  FOOD_UNKNOWN_PRESENCE_OPTIONS.find((o) => o.value === value)?.label || `Policy ${value}`

const formatDay = (value?: string) => {
  if (!value) return '—'
  return String(value).slice(0, 10)
}

const historyColumns: IHonestTableColumn<IFoodConfigVersion>[] = [
  {
    id: 'id',
    header: 'Version',
    sticky: true,
    cell: (row) => <BodyText weight='600'>#{row.id}</BodyText>,
  },
  {
    id: 'effectiveFromOperationalDay',
    header: 'Effective from',
    cell: (row) => formatDay(row.effectiveFromOperationalDay),
  },
  {
    id: 'createdAt',
    header: 'Created',
    cell: (row) => formatDay(row.createdAt),
  },
  {
    id: 'createdBy',
    header: 'Created by',
    cell: (row) => `User #${row.createdBy}`,
  },
  {
    id: 'policy',
    header: 'Site policy snapshot',
    minWidth: '18rem',
    cell: (row) => {
      const policy = row.payload?.policy
      if (!policy) return <OtherText grey>—</OtherText>
      return (
        <OtherText grey>
          {`${policy.maxMealsPerDay} meals/day · ${foodProvisioningModeLabel(policy.defaultMode)} · ${unknownPresenceLabel(policy.unknownPresencePolicy)}`}
        </OtherText>
      )
    },
  },
  {
    id: 'snapshot',
    header: 'Snapshot counts',
    minWidth: '14rem',
    cell: (row) => {
      const payload = row.payload
      if (!payload) return <OtherText grey>—</OtherText>
      return (
        <OtherText grey>
          {`${payload.kitchens?.length || 0} kitchens · ${payload.mealWindows?.length || 0} windows · ${payload.catchmentRules?.length || 0} catchments`}
        </OtherText>
      )
    },
  },
]

export const HistoryTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { configVersions, isLoading } = useSelector((store: RootState) => store.food)

  useEffect(() => {
    dispatch(getFoodConfigVersions({ page: 1, limit: 50 }))
  }, [dispatch])

  return (
    <HonestTable
      columns={historyColumns}
      data={configVersions.data || []}
      keyField='id'
      loading={isLoading}
      emptyMessage='No configuration versions yet — saving kitchens, windows, tags, catchments or site policy will stamp one'
    />
  )
}
